import { sumObjectArrayProperty } from '../utils/sumObjectArrayProperty'
import { doesQualifyToBeHidden } from './ItemAvailabilityStatus'
import {
  getUniqueSuppliersFromMSServices,
  isMSServiceWithProductLevelItem,
} from './MSService'
import { countUniqueStatii } from './StatiiCounts'
import { getTermBreakdown } from './TermBreakdown'
import { countUniqueLocations } from './LocationCounts'
import {
  getCountableMSServices,
  isCountableMSService,
  isSupersededMSService,
} from './MSServices'
import type {
  IOpportunity,
  IOpportunityMasterService,
  IOpportunityMSService,
  IOpportunityOverview,
} from '~/types/opportunity/models'
import type { TMSService } from '~/types/msService/models'
import type { IContract } from '~/types/contract/models'
import { assertIsNotUndefined, TEntityId } from '~/types/models'
import {
  OpportunityMasterServicesStatusIds,
  getOpportunityMasterServiceStatusById,
} from '~/services/selectLists/opportunityMasterServicesStatii'
import {
  OpportunityStatusIds,
  getOpportunityStatusById,
} from '~/services/selectLists/opportunityStatii'
import { OpportunityMSServicesStatusIds } from '~/services/selectLists/opportunityMSServicesStatii'
import { ProductLevelIds } from '~/services/selectLists/productLevels'
import * as KitService from '~/cynqueModules/dataObjects/KitService'
import { ContractTypeIds } from '~/services/selectLists/contractTypes'
import {
  ContractStatusIds,
  getContractStatusById,
} from '~/services/selectLists/contractStatii'

const mn = '[cynqueModules/dataObjects/OpportunityMSService.ts]'

// Propagators

/**
 * The msServices whose amounts roll up into a parent: included, and not CLOSED.
 * Extracted so the opportunity-level and master-service-level roll-ups filter
 * once and share the subset across their sums.
 *
 * ⚠ Terminal status here is CLOSED, not DISCONNECTED as in the Company cascade.
 *
 * ⚠ Also excludes every row that qualifies to be hidden — not available,
 * declined, swapped out, removed. A hidden msService keeps its amounts, so
 * without this the money it represents keeps counting on a row that is not part
 * of the configuration.
 *
 * 🔑 `doesQualifyToBeHidden`, NOT `isSupersededMSService`. Those differ by the
 * two legacy states — NOT_AVAILABLE and OPTIONAL_NOT_INCLUDED — and this path
 * used to admit both, while the kit and card totals excluded them. So the two
 * client money paths disagreed with each other about a declined row, and the
 * narrower test here is what made that true. The API's amount views take the
 * same widening, so client and server agree on one rule: hidden is not counted.
 *
 * ⚠ WHAT THIS DOES AND DOES NOT MOVE. MRC does not move at all: a hidden row is
 * quantity-zeroed by `deriveMSServiceQuantity` and mrcAmount is quantity x rate,
 * so it was already contributing zero. NRC, one-time and TCV are the real delta
 * — none of them passes through quantity (nrcAmount is `1 x unitNrcAmount`
 * whenever doMultiplyNrcByQuantity is false, oneTimeAmount is stored outright,
 * and tcvAmount collapses to oneTimeAmount once mrcAmount is zero). ⚠ The mock
 * fixture has every unitNrcAmount and oneTimeAmount at zero, so a green suite
 * says nothing here; the delta has to be measured against real data.
 */
function getMSServicesToRollUp(
  msServices: IOpportunityMSService[]
): IOpportunityMSService[] {
  return msServices.filter(
    (omss) =>
      omss.doInclude &&
      omss.status.id !== OpportunityMSServicesStatusIds.CLOSED &&
      !doesQualifyToBeHidden(omss)
  )
}

/**
 * The five status questions both Opportunity ladders ask, answered in ONE pass.
 *
 * Each was previously its own `.some()` over the full array, all five evaluated
 * eagerly before an if/else chain that usually settles on its first or second
 * arm — so a master service paid five full scans of its msServices, and the
 * opportunity five more of the flat list, to answer five booleans.
 *
 * `didFindIncludedOtherThanClosedWon` is the one that is not a pure function of
 * the status ids present: it also reads `doInclude`. That is why this returns a
 * flag set rather than a Set of ids.
 */
function getStatusFlags(msServices: IOpportunityMSService[]) {
  let didFindClosedWon = false
  let didFindClosedOrClosedWon = false
  let didFindOtherThanClosedOrClosedWon = false
  let didFindReopened = false
  let didFindIncludedOtherThanClosedWon = false

  for (const omss of msServices) {
    // ⚠ A superseded row is not evidence of anything. Counted, it would set
    // `didFindOtherThanClosedOrClosedWon` permanently, so an opportunity whose
    // live services are all Closed/Won would read "Some Closed Won" forever.
    //
    // ⚠ It matters MORE than it used to. The marker is an itemAvailabilityState
    // now, so the row carries a real, ordinary lifecycle status -- drop this and
    // the ladder comes out subtly wrong rather than visibly stuck.
    //
    // ⚠ The server does NOT skip it. propagate_omss_status() aggregates over
    // every row with no availability filter, so the two can disagree; filed as
    // WXTG/cynque-api-server#1569.
    if (isSupersededMSService(omss)) {
      continue
    }
    const statusId = omss.status.id
    const isClosedWon = statusId === OpportunityMSServicesStatusIds.CLOSED_WON
    const isClosed = statusId === OpportunityMSServicesStatusIds.CLOSED
    if (isClosedWon) {
      didFindClosedWon = true
    }
    if (isClosed || isClosedWon) {
      didFindClosedOrClosedWon = true
    } else {
      didFindOtherThanClosedOrClosedWon = true
    }
    if (statusId === OpportunityMSServicesStatusIds.REOPENED) {
      didFindReopened = true
    }
    if (omss.doInclude && !isClosedWon) {
      didFindIncludedOtherThanClosedWon = true
    }
  }

  return {
    didFindClosedWon,
    didFindClosedOrClosedWon,
    didFindOtherThanClosedOrClosedWon,
    didFindReopened,
    didFindIncludedOtherThanClosedWon,
  }
}

export function propagateOMSServicesData({
  overviewData,
  allOMSServices,
  setUpOpportunityMSServices,
}: {
  overviewData: IOpportunityOverview
  allOMSServices: IOpportunityMSService[]
  setUpOpportunityMSServices: Function
}) {
  //
  // embedded functions
  //
  function _propagateFromOMSServicesToOpportunity({
    mn,
    opportunity,
    allOMSServices,
  }: {
    mn: string
    opportunity: IOpportunity
    allOMSServices: IOpportunityMSService[]
  }) {
    function _getRecursPaidDescr(allOMSServices: IOpportunityMSService[]) {
      // One pass, short-circuiting on the first disagreement. This used to
      // `find` the reference value and then re-`filter` the whole array with the
      // identical predicate before `every`.
      let firstValidRecursPaidDescr: string | undefined
      for (const omss of allOMSServices) {
        if (
          !omss.doInclude ||
          omss.recursPaidDescr === '--' ||
          doesQualifyToBeHidden(omss)
        ) {
          continue
        }
        if (firstValidRecursPaidDescr === undefined) {
          firstValidRecursPaidDescr = omss.recursPaidDescr
        } else if (omss.recursPaidDescr !== firstValidRecursPaidDescr) {
          return 'Varies'
        }
      }
      return firstValidRecursPaidDescr ?? '--'
    }
    //
    // Execution starts here
    //
    const fn = `${mn} _propagateFromOMSServicesToOpportunity |`

    // One pass for all of them. These were five separate `.some()` scans of the
    // full array, every one computed eagerly to feed an if/else chain that
    // usually settles on its first or second arm.
    const {
      didFindClosedWon: didFindOMSSForThisOpportunityWithClosedWonStatus,
      didFindClosedOrClosedWon:
        didFindOMSSForThisOpportunityWithClosedOrClosedWonStatus,
      didFindOtherThanClosedOrClosedWon:
        didFindOMSSForThisOpportunityWithOtherThanClosedOrClosedWonStatus,
      didFindReopened: didFindOMSSForThisOpportunityWithReopenedStatus,
    } = getStatusFlags(allOMSServices)

    if (
      didFindOMSSForThisOpportunityWithReopenedStatus &&
      didFindOMSSForThisOpportunityWithClosedWonStatus
    ) {
      opportunity.status = getOpportunityStatusById(
        OpportunityStatusIds.REOPENED_SOME_CLOSED_WON
      )
    } else if (
      didFindOMSSForThisOpportunityWithReopenedStatus &&
      didFindOMSSForThisOpportunityWithClosedOrClosedWonStatus
    ) {
      opportunity.status = getOpportunityStatusById(
        OpportunityStatusIds.REOPENED_SOME_CLOSED
      )
    } else if (
      didFindOMSSForThisOpportunityWithClosedWonStatus &&
      didFindOMSSForThisOpportunityWithOtherThanClosedOrClosedWonStatus
    ) {
      opportunity.status = getOpportunityStatusById(
        OpportunityStatusIds.SOME_CLOSED_WON
      )
    } else if (
      didFindOMSSForThisOpportunityWithClosedOrClosedWonStatus &&
      didFindOMSSForThisOpportunityWithOtherThanClosedOrClosedWonStatus
    ) {
      opportunity.status = getOpportunityStatusById(
        OpportunityStatusIds.SOME_CLOSED
      )
    } else if (didFindOMSSForThisOpportunityWithReopenedStatus) {
      opportunity.status = getOpportunityStatusById(
        OpportunityStatusIds.REOPENED
      )
    } else if (
      didFindOMSSForThisOpportunityWithClosedWonStatus &&
      !didFindOMSSForThisOpportunityWithOtherThanClosedOrClosedWonStatus
      // The master-service ladder additionally requires
      // `!didFindIncludedOtherThanClosedWon` here; this one deliberately does
      // not. getStatusFlags returns that flag if it is ever wanted back.
    ) {
      opportunity.status = getOpportunityStatusById(
        OpportunityStatusIds.CLOSED_WON
      )
    } else if (
      didFindOMSSForThisOpportunityWithClosedOrClosedWonStatus &&
      !didFindOMSSForThisOpportunityWithOtherThanClosedOrClosedWonStatus
    ) {
      opportunity.status = getOpportunityStatusById(OpportunityStatusIds.CLOSED)
    }

    // Error trap
    if (!opportunity.status) {
      console.error(
        `${fn} Could not set opportunity status. Setting to 'NOT_SET'.`
      )
      opportunity.status = getOpportunityStatusById(
        OpportunityStatusIds.NOT_SET
      )
    }
    //

    // All four amounts share one predicate, so filter ONCE and sum the subset
    // instead of re-filtering the whole array inside each sum.
    const omsServicesToRollUp = getMSServicesToRollUp(allOMSServices)
    opportunity.mrcAmount = sumObjectArrayProperty(
      fn,
      omsServicesToRollUp,
      'mrcAmount'
    )
    opportunity.nrcAmount = sumObjectArrayProperty(
      fn,
      omsServicesToRollUp,
      'nrcAmount'
    )
    opportunity.oneTimeAmount = sumObjectArrayProperty(
      fn,
      omsServicesToRollUp,
      'oneTimeAmount'
    )
    opportunity.tcvAmount = sumObjectArrayProperty(
      fn,
      omsServicesToRollUp,
      'tcvAmount'
    )
    console.debug(
      `${fn} mrc ${opportunity.mrcAmount}, nrc ${opportunity.nrcAmount},` +
        ` 1x ${opportunity.oneTimeAmount}, tcv ${opportunity.tcvAmount}`
    )
    opportunity.recursPaidDescr = _getRecursPaidDescr(allOMSServices)
    opportunity.suppliers = getUniqueSuppliersFromMSServices(allOMSServices)
  }

  function _propagateFromOMSServicesToOMS({
    mn,
    allOMS,
  }: {
    mn: string
    allOMS: IOpportunityMasterService[]
  }) {
    const fn = `${mn} _propagateFromOMSServicesToOMS |`
    allOMS.forEach((oms: IOpportunityMasterService) => {
      console.debug(
        `${fn} oms: ${oms.descr}` +
          ` (${oms.opportunityMSServices?.length ?? 0} services)`
      )
      //
      const indexOfFirstOMSSWIthProductLevelItem = oms.opportunityMSServices
        ? oms.opportunityMSServices.findIndex(isMSServiceWithProductLevelItem)
        : -1
      console.debug(
        `${fn} indexOfFirstOMSSWIthProductLevelItem:`,
        indexOfFirstOMSSWIthProductLevelItem
      )
      if (indexOfFirstOMSSWIthProductLevelItem === -1) {
        console.debug(
          `${fn} indexOfFirstOMSSWIthProductLevelItem is -1.` +
            ` Setting status to NO_SERVICES`
        )
        oms.term = '--'
        oms.termBreakdown = undefined
        oms.status = getOpportunityMasterServiceStatusById(
          OpportunityMasterServicesStatusIds.NO_SERVICES
        )
        return
      }
      // If we get here, the oms has msServices, but Typescript doesn't know.
      // So...
      assertIsNotUndefined(
        oms.opportunityMSServices,
        fn,
        'Typescript guard. Should never happen.'
      )
      const omssArray = oms.opportunityMSServices // To keep Typescript happy below

      //
      // const propagateStatusArray: TPotentialStatus[] = [
      //   {
      //     statusId: OpportunityMSServicesStatusIds.READY_FOR_CONTRACT,
      //     statusIdForOnlyMssStatus:
      //       OpportunityMasterServicesStatusIds.READY_FOR_CONTRACTS,
      //     statusIdForOtherFound:
      //       OpportunityMasterServicesStatusIds.SOME_READY_FOR_CONTRACTS,
      //   },
      // ]
      //
      // One pass, as at the opportunity level above.
      const {
        didFindClosedOrClosedWon:
          didFindOMSSForThisOMSWithClosedOrClosedWonStatus,
        didFindClosedWon: didFindOMSSForThisOMSWithClosedWonStatus,
        didFindOtherThanClosedOrClosedWon:
          didFindOMSSForThisOMSWithOtherThanClosedOrClosedWonStatus,
        didFindReopened: didFindOMSSForThisOMSWithReopenedStatus,
        didFindIncludedOtherThanClosedWon:
          didFindIncludedOMSSForThisOMSWithOtherThanClosedWonStatus,
      } = getStatusFlags(omssArray)

      // set a default in case nothing qualifies
      oms.status = getOpportunityMasterServiceStatusById(
        OpportunityMasterServicesStatusIds.IN_PROGRESS
      )

      if (
        didFindOMSSForThisOMSWithReopenedStatus &&
        didFindOMSSForThisOMSWithClosedWonStatus
      ) {
        oms.status = getOpportunityMasterServiceStatusById(
          OpportunityMasterServicesStatusIds.REOPENED_SOME_CLOSED_WON
        )
      } else if (
        didFindOMSSForThisOMSWithReopenedStatus &&
        didFindOMSSForThisOMSWithClosedOrClosedWonStatus
      ) {
        oms.status = getOpportunityMasterServiceStatusById(
          OpportunityMasterServicesStatusIds.REOPENED_SOME_CLOSED
        )
      } else if (
        didFindOMSSForThisOMSWithClosedOrClosedWonStatus &&
        !didFindOMSSForThisOMSWithOtherThanClosedOrClosedWonStatus &&
        !didFindIncludedOMSSForThisOMSWithOtherThanClosedWonStatus
      ) {
        oms.status = getOpportunityMasterServiceStatusById(
          OpportunityMasterServicesStatusIds.CLOSED_WON
        )
      } else if (
        didFindOMSSForThisOMSWithClosedWonStatus &&
        didFindOMSSForThisOMSWithOtherThanClosedOrClosedWonStatus
      ) {
        oms.status = getOpportunityMasterServiceStatusById(
          OpportunityMasterServicesStatusIds.SOME_CLOSED_WON
        )
      } else if (
        didFindOMSSForThisOMSWithClosedOrClosedWonStatus &&
        didFindOMSSForThisOMSWithOtherThanClosedOrClosedWonStatus
      ) {
        oms.status = getOpportunityMasterServiceStatusById(
          OpportunityMasterServicesStatusIds.SOME_CLOSED
        )
      } else if (didFindOMSSForThisOMSWithReopenedStatus) {
        oms.status = getOpportunityMasterServiceStatusById(
          OpportunityMasterServicesStatusIds.REOPENED
        )
      } else if (
        didFindOMSSForThisOMSWithClosedOrClosedWonStatus &&
        !didFindOMSSForThisOMSWithOtherThanClosedOrClosedWonStatus
      ) {
        oms.status = getOpportunityMasterServiceStatusById(
          OpportunityMasterServicesStatusIds.CLOSED
        )
      }
      //
      // if (
      //   oms.status.id !== OpportunityMasterServicesStatusIds.CLOSED &&
      //   oms.status.id !== OpportunityMasterServicesStatusIds.CLOSED_WON &&
      //   oms.status.id !==
      //     OpportunityMasterServicesStatusIds.REOPENED_SOME_CLOSED_WON &&
      //   oms.status.id !==
      //     OpportunityMasterServicesStatusIds.REOPENED_SOME_CLOSED &&
      //   oms.status.id !== OpportunityMasterServicesStatusIds.REOPENED
      // ) {
      //   oms.status = getOpportunityMasterServiceStatusById(
      //     OpportunityMasterServicesStatusIds.IN_PROGRESS
      //   )
      // }

      // Error trap. Currently UNREACHABLE — every path above sets oms.status
      // first (NO_SERVICES then `return`, or the unconditional IN_PROGRESS
      // default before the if-chain). So this is latent, not a live fault.
      //
      // It was still wrong, and dangerously so if the default above is ever
      // removed. This sets a MASTER SERVICE's status, so it needs the
      // master-service getter and enum; it previously used the OPPORTUNITY-level
      // pair, copied from the identical trap in
      // _propagateFromOMSServicesToOpportunity without switching either half.
      // The two NOT_SET constants do not share a value
      // (OpportunityMSServicesStatusIds.NOT_SET = 11,
      // OpportunityMasterServicesStatusIds.NOT_SET = 15), and looking up 11 in
      // the OPPORTUNITY list is a HIT rather than a miss — it resolves to
      // OpportunityStatusIds.CLOSED_WON. Reached, it would have stamped the
      // master service "Closed Won" and fed the SOME_CLOSED_WON /
      // REOPENED_SOME_CLOSED_WON roll-ups, with no error to show for it.
      //
      // TypeScript cannot catch this: both enums are numeric and the getters
      // take a plain number, so every wrong pairing typechecks.
      if (!oms.status) {
        console.error(
          `${fn} Could not set master service status. Setting to 'NOT_SET'.`
        )
        oms.status = getOpportunityMasterServiceStatusById(
          OpportunityMasterServicesStatusIds.NOT_SET
        )
      }
      //

      console.debug(`${fn} oms status is set to ${oms.status?.label}`)

      // Counted over exactly the # Srvs set, so the Term hover's counts add up
      // to that column. `term` stays 'Varies' when they differ; the card
      // shows `termBreakdown.majorityTerm`, a tie going to the group's default.
      const termBreakdown = getTermBreakdown(
        getCountableMSServices(omssArray),
        {
          preferredTerm: oms.defaultTerm,
        }
      )
      oms.term = termBreakdown.term
      oms.termBreakdown = termBreakdown.doesVary ? termBreakdown : undefined
      const omsServicesToRollUp = getMSServicesToRollUp(omssArray)
      oms.mrcAmount = sumObjectArrayProperty(
        fn,
        omsServicesToRollUp,
        'mrcAmount'
      )
      console.debug(`${fn} mrcAnount: ${oms.mrcAmount}`)
      oms.nrcAmount = sumObjectArrayProperty(
        fn,
        omsServicesToRollUp,
        'nrcAmount'
      )
      oms.oneTimeAmount = sumObjectArrayProperty(
        fn,
        omsServicesToRollUp,
        'oneTimeAmount'
      )

      // status counts
      const filteredOMSServices = getCountableMSServices(omssArray)
      oms.omssStatusCounts = countUniqueStatii(filteredOMSServices)
      oms.omssLocationCounts = countUniqueLocations(filteredOMSServices)
    })
  }

  function _propagateFromOMSServicesToContracts({
    mn,
    allContracts,
    allOMSServices,
  }: {
    mn: string
    allContracts: IContract[]
    allOMSServices: TMSService[]
  }) {
    const fn = `${mn} _propagateFromOMSServicesToContracts |`

    // Count the countable msServices per contract in ONE pass. This used to
    // filter the entire array once per contract — O(contracts x msServices).
    const countableCountByContractId = new Map<TEntityId, number>()
    for (const omss of allOMSServices) {
      const contractId = omss.contract?.id
      if (contractId === undefined || !isCountableMSService(omss)) {
        continue
      }
      countableCountByContractId.set(
        contractId,
        (countableCountByContractId.get(contractId) ?? 0) + 1
      )
    }

    // ⚠ MSAs are skipped ENTIRELY, so an MSA never gets
    // numOpportunityMSServices set at all. The Company cascade instead visits
    // every contract and gates only the status write on contractType === SA.
    // The difference is deliberate — see OpportunityMSService.test.ts.
    //
    // ⚠ OPEN QUESTION — SA / Non-Contract audit, 2026-08-20. Deferred, not
    // settled: because this filters on "not MSA", a Non-Contract DOES get its
    // status re-derived here. Company and Order gate the identical rule on
    // `=== SA`, so they skip Non-Contract. One of the two readings is wrong;
    // deciding which needs additional logic, so both stand for now. All three
    // are pinned by their co-located specs.
    allContracts
      .filter(
        (contract: IContract) =>
          contract.contractType.id !== ContractTypeIds.MSA
      )
      .forEach((contract: IContract) => {
        contract.numOpportunityMSServices =
          countableCountByContractId.get(contract.id) ?? 0
        console.debug(
          `${fn} ${contract.hrid} (${contract.contractType.label}):` +
            ` ${contract.numOpportunityMSServices} services`
        )
        if (!contract.numOpportunityMSServices) {
          contract.status = getContractStatusById(ContractStatusIds.NO_SERVICES)
        } else if (contract.status.id === ContractStatusIds.NO_SERVICES) {
          contract.status = getContractStatusById(ContractStatusIds.DRAFT)
        }
      })
    console.debug(`${fn} propagated to ${allContracts.length} contracts`)
  }
  //
  // execution starts here...
  //
  const fn = `${mn} propagateOMSServicesData |`
  console.debug(`${fn} called`)
  setUpOpportunityMSServices()
  _propagateFromOMSServicesToOMS({
    mn,
    allOMS: overviewData.opportunityMasterServices,
  })
  _propagateFromOMSServicesToOpportunity({
    mn,
    opportunity: overviewData.opportunity,
    allOMSServices,
  })
  _propagateFromOMSServicesToContracts({
    mn,
    allContracts: overviewData.contracts,
    allOMSServices,
  })
}

export function updateKitItemsDoIncludeLocally({
  callingFn,
  updatedEntityData,
  entityId,
  opportunityMSServices,
}: {
  callingFn: string
  updatedEntityData: Partial<IOpportunityMSService>
  entityId: TEntityId
  opportunityMSServices: IOpportunityMSService[]
}) {
  const fn = `${callingFn} --> ${mn} updateKitItemsDoInclude |`
  if (!updatedEntityData.hasOwnProperty('doInclude')) {
    throw new Error(
      `${fn} called with updatedEntityData lacking 'doInclude' property`
    )
  }
  const updatedOmss = opportunityMSServices.find((omss) => omss.id === entityId)
  console.debug(`${fn} updatedOmss:`, updatedOmss)
  if (!updatedOmss) {
    throw new Error(`${fn} Passed an entityId for a non-existent omss.`)
  }

  if (updatedOmss.product.level.id === ProductLevelIds.ITEM) {
    return
  }
  const kitItems = KitService.getServicesForKitServiceFromProvidedServices({
    providedServices: opportunityMSServices,
    kitService: updatedOmss,
    doIncludeKitService: true,
    doIncludeDoesQualifyToBeHidden: true,
  })

  console.debug(`${fn} Kit items:`, kitItems)
  kitItems.forEach((item) => {
    /**
     * ⚠ A superseded row keeps `doInclude: false`, and this is the in-memory
     * half of the same rule the PATCH follows in
     * `makeEditKitPreSaveProcessorOverride`. Superseding set the flag false, and
     * it is what keeps the row out of the API's NRC / one-time / TCV roll-ups
     * until cynque-api-server#1559 lands -- the six amount views filter on
     * `do_include` alone. Skipped here rather than filtered out of the walk
     * above, because everything else about the cascade still applies to it.
     */
    if (isSupersededMSService(item)) {
      return
    }
    item.doInclude = updatedOmss.doInclude
  })
  console.debug(`${fn} Kit items:`, kitItems)
}
